import { memo, useDeferredValue, useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import {
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  Copy,
  Link as LinkIcon,
  Mail,
  MailX,
  RefreshCw,
  ScrollText,
  Trash2,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  UserAvatar,
  cn,
} from "@nexus/ui";
import { OrgLogoMark } from "./OrgLogoMark";
import { TableEmptyState } from "./shared/TableEmptyState";
import { RowActionsTrigger } from "./shared/RowActionsTrigger";
import { SortHeader } from "./shared/table/SortHeader";
import { useTableVirtualizer } from "./shared/table/useTableVirtualizer";
import type { InviteListItem, InviteStatus } from "@/features/identity-hub/api/identity-hub.api";
import { relativeTime, fullTimestamp } from "@/features/identity-hub/utils/time";
import { translateInviteStatusVisual } from "@/features/identity-hub/utils/status";

// ─── Sort ─────────────────────────────────────────────────────────────────────
// `email` / `createdAt` / `expiresAt` are ordered server-side. `status` and
// `sentBy` aren't expressible in the listing query, so the dashboard sorts the
// loaded rows client-side by name. Org and role stay static.

export type InviteSortKey = "email" | "createdAt" | "expiresAt" | "status" | "sentBy";

export interface InviteSortState {
  key: InviteSortKey;
  dir: "asc" | "desc";
}

// ─── i18n fallbacks ──────────────────────────────────────────────────────────
// Localised UI strings shipped with the component so the table never renders
// raw key paths if the locale bundle hasn't resolved yet.
const COLUMN_FALLBACKS: Record<string, string> = {
  email:  "Email",
  status: "Status",
  org:    "Organization",
  role:   "Role",
  sentBy: "Sent by",
  sentAt: "Sent",
  expires: "Expires",
};
const ACTION_FALLBACKS: Record<string, string> = {
  copyLink:        "Copy invite link",
  resendEmail:     "Resend email",
  regenerateLink:  "Regenerate link",
  viewAudit:       "View audit log",
  revoke:          "Revoke invite",
  delete:          "Delete record",
  copyLinkTip:       "Copies the existing invite link to your clipboard. No email is sent and the link stays valid.",
  resendEmailTip:    "Re-sends the invite email with the same link to the recipient. The link and its expiry don't change.",
  regenerateLinkTip: "Creates a new invite link and immediately invalidates the old one. Anyone with the previous link can no longer use it.",
  viewAuditTip:      "Opens the audit log filtered to this invite's email.",
  revokeTip:         "Cancels this invite so the link stops working immediately. The person can't accept it.",
  deleteTip:         "Permanently removes this invite record from the list.",
};

// ─── Status pill ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: InviteStatus }) {
  const { t } = useTranslation();
  const cfg = translateInviteStatusVisual(t, status);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn(
          "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset",
          cfg.pill,
          cfg.text,
        )}>
          <span className={cn("inline-block h-1.5 w-1.5 shrink-0 rounded-full", cfg.dot)} />
          {cfg.label}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[240px]">{cfg.desc}</TooltipContent>
    </Tooltip>
  );
}

// ─── Access pairing ───────────────────────────────────────────────────────────
//
// An invite can grant several (org, role) pairs. Rather than showing a deduped
// org cluster in one column and a stack of roles in another — which makes it
// impossible to tell *which role belongs to which org* — the Organization and
// Role columns render as stacked rows in the same order, so line N of the org
// column lines up with line N of the role column.

const MAX_VISIBLE_ACCESSES = 3;

interface AccessPair {
  key: string;
  tenantId: string;
  orgName: string;
  roleName: string;
  logoUrl?: string | null;
}

// Single-line height shared by the org and role rows so the two columns stay
// visually aligned line-for-line.
const ACCESS_ROW_CLASS = "flex h-6 items-center";

// ─── Org line ──────────────────────────────────────────────────────────────

function OrgLine({ pair }: { pair: AccessPair }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn(ACCESS_ROW_CLASS, "max-w-[180px] gap-1.5 text-xs font-medium text-foreground")}>
          <OrgLogoMark name={pair.orgName} logoUrl={pair.logoUrl} size={18} />
          <span className="truncate">{pair.orgName}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent>{pair.orgName}</TooltipContent>
    </Tooltip>
  );
}

// ─── Role badge ──────────────────────────────────────────────────────────────
//
// `h-6` matches `OrgLine` so paired rows align. The badge sizes to its content
// (the parent stack uses `items-start`, so it isn't stretched to column width)
// and carries a border so it reads as a chip even on muted surfaces.

function RoleBadge({ name }: { name: string }) {
  return (
    <span className="inline-flex h-6 max-w-[160px] items-center truncate rounded-md border border-border bg-muted px-2 text-xs font-medium text-foreground">
      {name}
    </span>
  );
}

// ─── Expiry cell ─────────────────────────────────────────────────────────────
//
// Avoids duplicating the StatusBadge: when the invite is already expired or
// accepted, this column shows a *time* (when it expired / when it was used),
// not the status label.

type ExpiryTone = "neutral" | "warn" | "danger" | "success";

const EXPIRY_TONE_TEXT: Record<ExpiryTone, string> = {
  neutral: "text-muted-foreground",
  warn:    "text-amber-700 dark:text-amber-300",
  danger:  "text-rose-700 dark:text-rose-300",
  success: "text-emerald-700 dark:text-emerald-300",
};
const EXPIRY_TONE_FILL: Record<ExpiryTone, string> = {
  neutral: "bg-emerald-500",
  warn:    "bg-amber-500",
  danger:  "bg-rose-500",
  success: "bg-emerald-500",
};

// Resolves the per-status visual: every state (pending / accepted / expired)
// renders the same bar so accepted and expired get the same animated "draw-in"
// affordance as a live countdown — the icon and fill colour distinguish them.
function resolveExpiryVariant(
  invite: InviteListItem,
  te: (key: string, opts?: Record<string, unknown>) => string,
): {
  tone: ExpiryTone;
  barPct: number;
  showBar: boolean;
  label: string;
  tooltip: string;
  icon: ReactNode;
} {
  const expires = new Date(invite.expiresAt).getTime();

  if (invite.status === "accepted") {
    return {
      tone: "success",
      barPct: 100,
      showBar: false,
      label: invite.usedAt ? te("acceptedAgo", { time: relativeTime(invite.usedAt) }) : te("accepted"),
      tooltip: invite.usedAt ? te("acceptedTooltipAgo", { time: fullTimestamp(invite.usedAt) }) : te("acceptedTooltip"),
      icon: <CheckCircle2 className="h-3.5 w-3.5 shrink-0 animate-in zoom-in-50 fade-in duration-500" />,
    };
  }

  if (invite.status === "expired") {
    return {
      tone: "danger",
      barPct: 0,
      showBar: false,
      label: te("expiredAgo", { time: relativeTime(invite.expiresAt) }),
      tooltip: te("expiredTooltip", { time: fullTimestamp(invite.expiresAt) }),
      icon: <MailX className="h-3.5 w-3.5 shrink-0 animate-in fade-in slide-in-from-left-1 duration-500" />,
    };
  }

  // Pending: bar tracks the fraction of lifetime still remaining.
  const created = new Date(invite.createdAt).getTime();
  const lifetime = Math.max(expires - created, 1);
  const remaining = Math.max(Math.min(expires - Date.now(), lifetime), 0);
  const days  = Math.ceil((expires - Date.now()) / 86_400_000);
  const hours = Math.ceil((expires - Date.now()) / 3_600_000);

  const tone: ExpiryTone = days < 3 ? "danger" : days < 7 ? "warn" : "neutral";
  return {
    tone,
    barPct: Math.round((remaining / lifetime) * 100),
    showBar: true,
    label: days <= 0 ? te("hoursLeft", { hours: Math.max(hours, 0) }) : te("daysLeft", { days }),
    tooltip: te("expiresTooltip", { time: fullTimestamp(invite.expiresAt) }),
    icon: <Clock className="h-3 w-3 shrink-0 opacity-60" />,
  };
}

function ExpiryCell({ invite }: { invite: InviteListItem }) {
  const { t } = useTranslation();
  const te = (key: string, opts?: Record<string, unknown>) => t(`iam.invites.expiry.${key}`, opts);
  const { tone, barPct, showBar, label, tooltip, icon } = resolveExpiryVariant(invite, te);

  // Animate the countdown fill in from empty on mount for a subtle "draw" effect.
  const [fillPct, setFillPct] = useState(0);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setFillPct(barPct));
    return () => cancelAnimationFrame(frame);
  }, [barPct]);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className={cn("flex max-w-full cursor-default flex-col gap-1.5", showBar && "w-[120px]")}>
          <span className={cn("inline-flex items-center gap-1 whitespace-nowrap text-xs tabular-nums", EXPIRY_TONE_TEXT[tone])}>
            {icon}
            {label}
          </span>
          {showBar && (
            <div
              role="progressbar"
              aria-valuenow={barPct}
              aria-valuemin={0}
              aria-valuemax={100}
              className="h-1 w-full overflow-hidden rounded-full bg-muted-foreground/20"
            >
              <div
                className={cn("h-full rounded-full transition-[width] duration-700 ease-out", EXPIRY_TONE_FILL[tone])}
                style={{ width: `${fillPct}%` }}
              />
            </div>
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}

// ─── Sent-by cell ────────────────────────────────────────────────────────────

function SentByCell({ name, email, photoUrl, isSystem }: { name: string; email: string | null; photoUrl: string | null; isSystem: boolean }) {
  const { t } = useTranslation();
  if (isSystem) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-muted">
          <ScrollText className="h-3 w-3" />
        </span>
        {t("iam.invites.systemActor", "System")}
      </span>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <UserAvatar name={name} src={photoUrl} size="sm" className="shrink-0" />
      <div className="flex min-w-0 flex-col leading-tight">
        <span className="truncate text-xs font-medium text-foreground">{name}</span>
        {email && (
          <span className="truncate text-[10.5px] text-muted-foreground">{email}</span>
        )}
      </div>
    </div>
  );
}

// ─── Action menu item with explainer tooltip ─────────────────────────────────
// Wraps a DropdownMenuItem so hovering (or keyboard-focusing) it surfaces a
// tooltip describing exactly what the action does before the user commits.

function ActionItem({
  tip,
  variant,
  onClick,
  children,
}: {
  tip: string;
  variant?: "default" | "destructive";
  onClick?: () => void;
  children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <DropdownMenuItem className="gap-2" variant={variant} onClick={onClick}>
          {children}
        </DropdownMenuItem>
      </TooltipTrigger>
      <TooltipContent side="left" className="max-w-[260px]">
        {tip}
      </TooltipContent>
    </Tooltip>
  );
}

// ─── Copy-email button ───────────────────────────────────────────────────────
//
// Revealed on row hover (and on keyboard focus for a11y). Copies the invite's
// email to the clipboard, flips to a check for a beat, and confirms with a toast.

const COPY_FEEDBACK_MS = 1500;

function CopyEmailButton({ email }: { email: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (resetTimer.current) clearTimeout(resetTimer.current);
  }, []);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      if (resetTimer.current) clearTimeout(resetTimer.current);
      resetTimer.current = setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
      toast.success(t("iam.invites.toast.copyEmailSuccess", { defaultValue: "Email copied to clipboard" }));
    } catch {
      toast.error(t("iam.invites.toast.copyEmailError", { defaultValue: "Couldn't copy email" }));
    }
  }

  const label = copied
    ? t("iam.invites.actions.copied", { defaultValue: "Copied!" })
    : t("iam.invites.actions.copyEmail", { defaultValue: "Copy email" });

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          onClick={() => void handleCopy()}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-all hover:bg-muted hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-hover:opacity-100"
        >
          {copied
            ? <Check className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
            : <Copy className="h-3.5 w-3.5" />}
        </button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

// ─── Row (memoized) ──────────────────────────────────────────────────────────
//
// Pulled out of the table body so React.memo can skip rows whose props are
// reference-stable. The parent passes id-keyed callbacks straight through —
// when only one row's `busyInviteId` flips, only that row re-renders instead
// of the entire visible window.

interface InviteRowProps {
  row: InviteListItem;
  isBusy: boolean;
  orgLogoUrl: string | null | undefined;
  orgLogosByTenantId?: Map<string, string | null | undefined>;
  ta: (key: string) => string;
  onCopyLink?:     (invite: InviteListItem) => void;
  onResend?:       (invite: InviteListItem) => void;
  onRegenerate?:   (invite: InviteListItem) => void;
  onRevoke?:       (invite: InviteListItem) => void;
  onDelete?:       (invite: InviteListItem) => void;
  onOpenAuditLog?: (options?: { highlightId?: string | null; searchPrefill?: string | null; category?: string | null }) => void;
}

function InviteRowImpl({
  row,
  isBusy,
  orgLogoUrl,
  orgLogosByTenantId,
  ta,
  onCopyLink,
  onResend,
  onRegenerate,
  onRevoke,
  onDelete,
  onOpenAuditLog,
}: InviteRowProps) {
  const { t } = useTranslation();
  const sentByName = row.createdByName ?? row.createdByEmail ?? t("iam.invites.systemActor", "System");
  const isSystem   = !row.createdByName && !row.createdByEmail;

  // Ordered (org, role) pairs powering the aligned Organization/Role columns.
  // Falls back to the legacy single-access fields when `accesses` is empty
  // (pre-multi-org invites).
  const accessPairs: AccessPair[] =
    row.accesses && row.accesses.length > 0
      ? row.accesses.map((ia) => ({
          key: ia.accessId,
          tenantId: ia.tenantId,
          orgName: ia.tenantName ?? ia.tenantId,
          roleName: ia.roleName ?? ia.accessName ?? "—",
          logoUrl: ia.tenantId ? orgLogosByTenantId?.get(ia.tenantId) ?? null : null,
        }))
      : row.tenantName || row.accessName
        ? [{
            key: row.accessId ?? row.tenantId ?? "legacy",
            tenantId: row.tenantId ?? "",
            orgName: row.tenantName ?? row.tenantId ?? "—",
            roleName: row.accessName ?? "—",
            logoUrl: orgLogoUrl ?? null,
          }]
        : [];
  const visiblePairs = accessPairs.slice(0, MAX_VISIBLE_ACCESSES);
  const extraPairs = accessPairs.length - visiblePairs.length;
  const canCopyLink = row.status !== "accepted" && !!onCopyLink;
  const canResend  = row.status === "pending"  && !!onResend;
  const canRegen   = row.status !== "accepted" && !!onRegenerate;
  const canRevoke  = row.status === "pending"  && !!onRevoke;
  const canDelete  = row.status === "expired"  && !!onDelete;

  return (
    <tr
      className={cn(
        "group transition-colors",
        isBusy ? "opacity-60 pointer-events-none" : "hover:bg-muted/30",
      )}
    >
      {/* Email + optional message */}
      <td className="px-3 py-2.5 align-middle">
        <div className="flex min-w-0 flex-col gap-0.5">
          <div className="flex min-w-0 items-center gap-1.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="truncate font-medium text-foreground">{row.email}</span>
              </TooltipTrigger>
              <TooltipContent side="top">{row.email}</TooltipContent>
            </Tooltip>
            <CopyEmailButton email={row.email} />
          </div>
          {row.message && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="line-clamp-1 text-[11px] text-muted-foreground">
                  "{row.message}"
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[320px] whitespace-pre-wrap">
                {row.message}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </td>

      {/* Status */}
      <td className="px-3 py-2.5 align-middle">
        <StatusBadge status={row.status} />
      </td>

      {/* Org(s) — stacked, aligned 1:1 with the Role column */}
      <td className="px-3 py-2.5 align-middle">
        {visiblePairs.length > 0 ? (
          <div className="flex flex-col items-start gap-1">
            {visiblePairs.map((pair) => (
              <OrgLine key={pair.key} pair={pair} />
            ))}
            {extraPairs > 0 && (
              <span className={cn(ACCESS_ROW_CLASS, "text-xs text-muted-foreground")}>
                {t("iam.invites.moreCount", { count: extraPairs })}
              </span>
            )}
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </td>

      {/* Role(s) — stacked, aligned 1:1 with the Organization column */}
      <td className="px-3 py-2.5 align-middle">
        {visiblePairs.length > 0 ? (
          <div className="flex flex-col items-start gap-1">
            {visiblePairs.map((pair) => (
              <RoleBadge key={pair.key} name={pair.roleName} />
            ))}
            {extraPairs > 0 && (
              <span className={cn(ACCESS_ROW_CLASS, "text-xs text-muted-foreground")}>
                {t("iam.invites.moreCount", { count: extraPairs })}
              </span>
            )}
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </td>

      {/* Sent by */}
      <td className="px-3 py-2.5 align-middle">
        <SentByCell name={sentByName} email={row.createdByEmail} photoUrl={row.createdByPhotoUrl} isSystem={isSystem} />
      </td>

      {/* Sent at */}
      <td className="px-3 py-2.5 align-middle">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-default text-xs tabular-nums text-muted-foreground decoration-dotted underline-offset-2 hover:text-foreground hover:underline">
              {relativeTime(row.createdAt)}
            </span>
          </TooltipTrigger>
          <TooltipContent>{fullTimestamp(row.createdAt)}</TooltipContent>
        </Tooltip>
      </td>

      {/* Expires */}
      <td className="px-3 py-2.5 align-middle">
        <ExpiryCell invite={row} />
      </td>

      {/* Actions */}
      <td className="px-2 py-2.5 align-middle">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <RowActionsTrigger />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            {canCopyLink && (
              <ActionItem tip={ta("copyLinkTip")} onClick={() => onCopyLink?.(row)}>
                <LinkIcon className="h-3.5 w-3.5" />
                {ta("copyLink")}
              </ActionItem>
            )}

            {canResend && (
              <ActionItem tip={ta("resendEmailTip")} onClick={() => onResend?.(row)}>
                <Mail className="h-3.5 w-3.5" />
                {ta("resendEmail")}
              </ActionItem>
            )}

            {canRegen && (
              <ActionItem tip={ta("regenerateLinkTip")} onClick={() => onRegenerate?.(row)}>
                <RefreshCw className="h-3.5 w-3.5" />
                {ta("regenerateLink")}
              </ActionItem>
            )}

            {onOpenAuditLog && (
              <ActionItem
                tip={ta("viewAuditTip")}
                onClick={() => onOpenAuditLog({ searchPrefill: row.email, category: "identity" })}
              >
                <ScrollText className="h-3.5 w-3.5" />
                {ta("viewAudit")}
              </ActionItem>
            )}

            {canRevoke && (
              <>
                <DropdownMenuSeparator />
                <ActionItem
                  tip={ta("revokeTip")}
                  variant="destructive"
                  onClick={() => onRevoke?.(row)}
                >
                  <XCircle className="h-3.5 w-3.5" />
                  {ta("revoke")}
                </ActionItem>
              </>
            )}

            {canDelete && (
              <>
                <DropdownMenuSeparator />
                <ActionItem
                  tip={ta("deleteTip")}
                  variant="destructive"
                  onClick={() => onDelete?.(row)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  {ta("delete")}
                </ActionItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  );
}

const InviteRow = memo(InviteRowImpl);

// ─── Main table ──────────────────────────────────────────────────────────────

const TABLE_COLUMN_COUNT = 8;

export interface InvitesTableProps {
  invites: InviteListItem[];
  loading?: boolean;
  /**
   * Optional tenant-id → logo URL map. When provided, the org cell renders the
   * actual org logo (matching PeopleTable). Falls back to coloured initials.
   */
  orgLogosByTenantId?: Map<string, string | null | undefined>;
  onCopyLink?:     (invite: InviteListItem) => void;
  onResend?:       (invite: InviteListItem) => void;
  onRegenerate?:   (invite: InviteListItem) => void;
  onRevoke?:       (invite: InviteListItem) => void;
  onDelete?:       (invite: InviteListItem) => void;
  onOpenAuditLog?: (options?: { highlightId?: string | null; searchPrefill?: string | null; category?: string | null }) => void;
  busyInviteId?:   string | null;
  /** Active sort + handler. When omitted, headers render static (non-sortable). */
  sort?:   InviteSortState;
  onSort?: (key: InviteSortKey) => void;
  /**
   * Vertical scroll container for row virtualization. The dashboard owns
   * the scroller (the `overflow-y-auto` wrapper around the tab content)
   * and passes its ref down — only the visible window of rows mounts,
   * regardless of how many invites a tenant has.
   */
  scrollContainerRef?: RefObject<HTMLElement | null>;
}

export function InvitesTable({
  invites,
  loading,
  orgLogosByTenantId,
  onCopyLink,
  onResend,
  onRegenerate,
  onRevoke,
  onDelete,
  onOpenAuditLog,
  busyInviteId,
  sort,
  onSort,
  scrollContainerRef,
}: InvitesTableProps) {
  const { t } = useTranslation();
  const tc = (key: string) =>
    t(`iam.invites.columns.${key}`, { defaultValue: COLUMN_FALLBACKS[key] ?? key });
  // Memoize the action-label helper so memoized rows see a stable reference
  // and React.memo can actually skip them when only `busyInviteId` changes.
  const ta = useMemo(
    () => (key: string) =>
      t(`iam.invites.actions.${key}`, { defaultValue: ACTION_FALLBACKS[key] ?? key }),
    [t],
  );

  // Defer the heavy `invites` array so React paints chrome urgently and
  // schedules the row reconciliation as low-priority interruptible work.
  const rows = useDeferredValue(invites);

  const tableRef = useRef<HTMLTableElement>(null);

  // Match PeopleTable: virtualize against the parent tab's scroller so
  // hundreds of invites render without mounting hundreds of `<tr>`s + Radix
  // DropdownMenu roots. Falls back to no-op virtualization when no ref is
  // provided (consumers that pass a small `invites` list can omit it).
  const { virtualizer, virtualItems, topPad, bottomPad } = useTableVirtualizer({
    count: rows.length,
    scrollContainerRef,
  });

  if (!loading && rows.length === 0) {
    return (
      <TableEmptyState
        icon={Mail}
        variant="dashed"
        title={t("iam.invites.empty.title", { defaultValue: "No invites match your filters" })}
        subtitle={t("iam.invites.empty.hint", { defaultValue: "Send a new invite or change the status filter." })}
      />
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="overflow-x-auto">
          {/* `min-w` keeps the 8 fixed-width columns readable on narrow
              viewports — below it the horizontal scroller engages instead of
              crushing columns into each other (mobile overlap). */}
          <table ref={tableRef} className="w-full min-w-[880px] table-fixed text-sm">
            {/* Fixed column widths so the layout never reflows as row content
                changes (sorting, org/role counts, the expiry bar, …). */}
            <colgroup>
              <col className="w-[19%]" />{/* email */}
              <col className="w-[10%]" />{/* status */}
              <col className="w-[15%]" />{/* organization */}
              <col className="w-[12%]" />{/* role */}
              <col className="w-[19%]" />{/* sent by */}
              <col className="w-[9%]" />{/* sent */}
              <col className="w-[12%]" />{/* expires */}
              <col className="w-[44px]" />{/* actions */}
            </colgroup>
            <thead className="sticky top-0 z-10 bg-card">
              <tr className="group border-b border-border bg-muted/30 text-left text-xs text-muted-foreground">
                {sort && onSort
                  ? <SortHeader id="email" label={tc("email")} sort={sort} onSort={onSort} />
                  : <th className="px-3 py-2.5">{tc("email")}</th>}
                {sort && onSort
                  ? <SortHeader id="status" label={tc("status")} sort={sort} onSort={onSort} />
                  : <th className="px-3 py-2.5">{tc("status")}</th>}
                <th className="px-3 py-2.5 font-normal">
                  <span className="inline-flex items-center gap-1">{tc("org")}</span>
                </th>
                <th className="px-3 py-2.5 font-normal">
                  <span className="inline-flex items-center gap-1">{tc("role")}</span>
                </th>
                {sort && onSort
                  ? <SortHeader id="sentBy" label={tc("sentBy")} sort={sort} onSort={onSort} />
                  : <th className="px-3 py-2.5">{tc("sentBy")}</th>}
                {sort && onSort
                  ? <SortHeader id="createdAt" label={tc("sentAt")} sort={sort} onSort={onSort} />
                  : <th className="px-3 py-2.5">{tc("sentAt")}</th>}
                {sort && onSort
                  ? <SortHeader id="expiresAt" label={tc("expires")} sort={sort} onSort={onSort} />
                  : <th className="px-3 py-2.5">{tc("expires")}</th>}
                <th aria-label={t("iam.invites.actionsHeader", "Actions")} className="w-10 px-2 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {topPad > 0 && (
                <tr aria-hidden style={{ height: topPad }}>
                  <td colSpan={TABLE_COLUMN_COUNT} style={{ padding: 0, border: 0 }} />
                </tr>
              )}
              {virtualItems.map((vi) => {
                const row = rows[vi.index];
                if (!row) return null;
                return (
                  <InviteRow
                    key={row.id}
                    row={row}
                    isBusy={busyInviteId === row.id}
                    orgLogoUrl={row.tenantId ? orgLogosByTenantId?.get(row.tenantId) ?? null : null}
                    orgLogosByTenantId={orgLogosByTenantId}
                    ta={ta}
                    onCopyLink={onCopyLink}
                    onResend={onResend}
                    onRegenerate={onRegenerate}
                    onRevoke={onRevoke}
                    onDelete={onDelete}
                    onOpenAuditLog={onOpenAuditLog}
                  />
                );
              })}
              {bottomPad > 0 && (
                <tr aria-hidden style={{ height: bottomPad }}>
                  <td colSpan={TABLE_COLUMN_COUNT} style={{ padding: 0, border: 0 }} />
                </tr>
              )}
              {loading && rows.length === 0 && (
                <tr>
                  <td colSpan={TABLE_COLUMN_COUNT} className="px-3 py-8 text-center text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-2">
                      <MailX className="h-3 w-3 animate-pulse" /> {t("iam.invites.loadingInvites", "Loading invites…")}
                    </span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between border-t border-border bg-muted/20 px-4 py-2 text-xs text-muted-foreground">
          <span>
            {t("iam.invites.footer.count", {
              count: rows.length,
              defaultValue: rows.length === 1 ? "1 invite" : `${rows.length} invites`,
            })}
          </span>
          {loading && rows.length > 0 && <span className="text-[10.5px] italic opacity-70">{t("iam.invites.refreshing", "refreshing…")}</span>}
        </div>
      </div>
    </TooltipProvider>
  );
}

